import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook för att hantera fade-effekt baserat på scrollposition
 * @param {React.RefObject} scrollRef - Ref till elementet som ska scrollas
 * @param {string} fadeElementId - ID för elementet som ska visa/dölja fadeen
 * @param {string} hiddenClassName - Klassnamn att lägga till/ta bort när scrollat till botten
 * @param {number} threshold - Antal pixlar från botten för att räknas som "vid botten"
 * @return {boolean} isAtBottom - Om elementet är scrollat till botten
 */
const useScrollFade = (
  scrollRef,
  fadeElementId = 'header-menu-fade',
  hiddenClassName = 'header-menu-fade--hidden',
  threshold = 5,
) => {
  const [isAtBottom, setIsAtBottom] = useState(false);

  const checkScrolledToBottom = useCallback(() => {
    if (!scrollRef.current) return false;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    return scrollTop + clientHeight >= scrollHeight - threshold;
  }, [scrollRef, threshold]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return undefined;

    const handleScroll = () => {
      const scrolledToBottom = checkScrolledToBottom();

      if (scrolledToBottom !== isAtBottom) {
        setIsAtBottom(scrolledToBottom);
      }

      const fadeElement = document.getElementById(fadeElementId);
      if (fadeElement) {
        if (scrolledToBottom) {
          fadeElement.classList.add(hiddenClassName);
        } else {
          fadeElement.classList.remove(hiddenClassName);
        }
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [
    checkScrolledToBottom,
    fadeElementId,
    hiddenClassName,
    isAtBottom,
    scrollRef,
  ]);

  return isAtBottom;
};

export default useScrollFade;
